// src/Dashboard/pages/teacher/Belts/hooks/usePassageCeintureDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getGroupsList } from "../../../../../services/groupServices";
import { getAllAdherentsListWithGroup } from "../../../../../services/adherentServices";

import {
  addBeltHistory,
  getBeltHistoryByAdherent,
  getBeltsList,
} from "../../../../../services/beltServices";

import { getSettings } from "../../../../../services/settingsServices";

import {
  buildSeasonOptions,
  calculateBeltStats,
  filterAdherentsForBelts,
  getTodayInputDate,
  sortAdherentsForBelts,
} from "../utils/beltDashboardUtils";

const defaultFilters = {
  query: "",
  season: "",
  group: "",
  belt: "",
  historyStatus: "All",
  sort: "name_asc",
};

const HISTORY_CHUNK_SIZE = 25;

async function loadHistoriesInChunks(adherents, season) {
  const map = {};
  const list = Array.isArray(adherents) ? adherents : [];

  for (let index = 0; index < list.length; index += HISTORY_CHUNK_SIZE) {
    const batch = list.slice(index, index + HISTORY_CHUNK_SIZE);

    const results = await Promise.all(
      batch.map((adherent) =>
        getBeltHistoryByAdherent(adherent.id, season || undefined)
          .then((history) => ({
            id: adherent.id,
            history: Array.isArray(history) ? history : [],
          }))
          .catch(() => ({
            id: adherent.id,
            history: [],
          }))
      )
    );

    results.forEach(({ id, history }) => {
      map[id] = history?.[0] || null;
    });
  }

  return map;
}

export function usePassageCeintureDashboard() {
  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [belts, setBelts] = useState([]);
  const [beltHistories, setBeltHistories] = useState({});
  const [seasons, setSeasons] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [bulkSaving, setBulkSaving] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBeltId, setBulkBeltId] = useState("");
  const [bulkDate, setBulkDate] = useState(getTodayInputDate());

  const [openedAdherent, setOpenedAdherent] = useState(null);

  const loadCoreData = useCallback(async () => {
    try {
      setLoading(true);

      const [settingsData, groupList, adherentsList, beltsList] =
        await Promise.all([
          getSettings(),
          getGroupsList(),
          getAllAdherentsListWithGroup(),
          getBeltsList(),
        ]);

      const settings = Array.isArray(settingsData)
        ? settingsData[0] || {}
        : settingsData || {};

      const activeSeason = settings?.saison_active || "";

      setGroups(Array.isArray(groupList) ? groupList : []);
      setAdherents(Array.isArray(adherentsList) ? adherentsList : []);
      setBelts(Array.isArray(beltsList) ? beltsList : []);

      setSeasons(buildSeasonOptions(activeSeason));

      setFilters((prev) => ({
        ...prev,
        season: prev.season || activeSeason || "",
      }));
    } catch (error) {
      console.error("Erreur chargement ceintures :", error);
      toast.error("Erreur lors du chargement des passages de grades.");
      setGroups([]);
      setAdherents([]);
      setBelts([]);
      setBeltHistories({});
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshHistories = useCallback(async () => {
    try {
      setLoading(true);

      const historiesMap = await loadHistoriesInChunks(adherents, filters.season);
      setBeltHistories(historiesMap);
    } catch (error) {
      console.error("Erreur chargement historiques :", error);
      toast.error("Erreur lors du chargement des historiques de ceinture.");
      setBeltHistories({});
    } finally {
      setLoading(false);
    }
  }, [adherents, filters.season]);

  const refreshAll = useCallback(async () => {
    await loadCoreData();
  }, [loadCoreData]);

  useEffect(() => {
    loadCoreData();
  }, [loadCoreData]);

  useEffect(() => {
    if (!adherents.length) {
      setBeltHistories({});
      return;
    }

    refreshHistories();
  }, [adherents, filters.season, refreshHistories]);

  const filteredAdherents = useMemo(() => {
    return sortAdherentsForBelts(
      filterAdherentsForBelts({
        adherents,
        histories: beltHistories,
        filters,
      }),
      filters.sort
    );
  }, [adherents, beltHistories, filters]);

  useEffect(() => {
    const visibleIds = new Set(filteredAdherents.map((adherent) => adherent.id));

    setSelectedIds((prev) => {
      const next = new Set();

      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        }
      });

      return next;
    });
  }, [filteredAdherents]);

  const stats = useMemo(() => {
    return calculateBeltStats({
      allAdherents: adherents,
      filteredAdherents,
      histories: beltHistories,
    });
  }, [adherents, filteredAdherents, beltHistories]);

  const allChecked =
    filteredAdherents.length > 0 &&
    filteredAdherents.every((adherent) => selectedIds.has(adherent.id));

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(filteredAdherents.map((adherent) => adherent.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkBeltId("");
    setBulkDate(getTodayInputDate());
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0) {
      toast.warn("Sélectionnez au moins un adhérent.");
      return;
    }

    if (!bulkBeltId) {
      toast.warn("Choisissez une ceinture.");
      return;
    }

    if (!bulkDate) {
      toast.warn("Choisissez une date de passage.");
      return;
    }

    const adherentIds = Array.from(selectedIds).map(Number);
    const beltId = Number(bulkBeltId);

    try {
      setBulkSaving(true);

      const results = await Promise.allSettled(
        adherentIds.map((adherentId) =>
          addBeltHistory({
            adherent_id: adherentId,
            belt_id: beltId,
            date_passage: bulkDate,
          })
        )
      );

      const successCount = results.filter(
        (result) => result.status === "fulfilled"
      ).length;

      const errorCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(
          `${successCount} passage${successCount > 1 ? "s" : ""} créé${
            successCount > 1 ? "s" : ""
          }${errorCount ? ` • ${errorCount} erreur(s)` : ""}.`
        );

        clearSelection();
        await refreshHistories();
      } else {
        toast.error("Aucun passage n’a pu être créé.");
      }
    } catch (error) {
      console.error("Erreur passage groupé :", error);
      toast.error("Erreur lors de l’application du passage groupé.");
    } finally {
      setBulkSaving(false);
    }
  };

  const openDrawer = (adherent) => {
    setOpenedAdherent(adherent);
  };

  const closeDrawer = () => {
    setOpenedAdherent(null);
  };

  return {
    groups,
    adherents,
    belts,
    beltHistories,
    filteredAdherents,
    filters,
    setFilters,
    seasons,
    stats,
    loading,
    selectedIds,
    allChecked,
    toggleAll,
    toggleOne,
    clearSelection,
    bulkBeltId,
    setBulkBeltId,
    bulkDate,
    setBulkDate,
    bulkSaving,
    applyBulk,
    openedAdherent,
    openDrawer,
    closeDrawer,
    refreshHistories,
    refreshAll,
  };
}